<template>
  <!-- Auto mode: JS truncation at sentence/word boundary (used for space-constrained contexts, e.g. the viewer modal) -->
  <template v-if="autoMode">
    <p class="rm-text">{{ displayText }}</p>
    <button v-if="showButton" class="rm-btn" :title="expanded ? 'Show less' : 'Read more'" :aria-label="expanded ? 'Show less' : 'Read more'" @click="expanded = !expanded">
      {{ expanded ? 'Show less' : 'Read more' }}
      <ChevronUpIcon v-if="expanded" class="rm-chevron" />
      <ChevronDownIcon v-else class="rm-chevron" />
    </button>
  </template>
  <!-- Plain mode: no marker, auto-truncation disabled — render full text -->
  <template v-if="plainMode">
    <p class="rm-text">{{ text }}</p>
  </template>
  <!-- Marker mode: editorial split at [readmore] marker -->
  <template v-if="markerMode">
    <p v-for="(para, i) in introParagraphs" :key="'i' + i" class="rm-para">{{ para }}</p>
    <button v-if="!expanded" class="rm-btn" title="Read more" aria-label="Read more" @click="expanded = true">
      Read more <ChevronDownIcon class="rm-chevron" />
    </button>
    <template v-if="expanded">
      <p v-for="(para, i) in extendedParagraphs" :key="'e' + i" class="rm-para">{{ para }}</p>
      <button class="rm-btn" title="Show less" aria-label="Show less" @click="expanded = false">
        Show less <ChevronUpIcon class="rm-chevron" />
      </button>
    </template>
  </template>
</template>

<script>
import ChevronDownIcon from '@/assets/icons/chevron-down.vue'
import ChevronUpIcon from '@/assets/icons/chevron-up.vue'

export default {
  name: 'ReadMore',
  components: { ChevronDownIcon, ChevronUpIcon },
  props: {
    text: { type: String, required: true },
    threshold: { type: Number, default: 200 },
    marker: { type: String, default: '' },
    autoTruncate: { type: Boolean, default: true }
  },
  data() {
    return { expanded: false }
  },
  computed: {
    markerMode() {
      return !!this.marker && this.text.includes(this.marker)
    },
    autoMode() {
      return !this.markerMode && this.autoTruncate
    },
    plainMode() {
      return !this.markerMode && !this.autoTruncate
    },
    showButton() {
      return this.text.length > this.threshold
    },
    displayText() {
      if (this.expanded || !this.showButton) return this.text
      const sliced = this.text.slice(0, this.threshold)
      const lastDot = sliced.lastIndexOf('.')
      const lastSpace = sliced.lastIndexOf(' ')
      if (lastDot > this.threshold - 100) return sliced.slice(0, lastDot + 1)
      if (lastSpace > 0) return sliced.slice(0, lastSpace) + '…'
      return sliced + '…'
    },
    introParagraphs() {
      const raw = this.text.split(this.marker)[0]
      return raw.split('\n\n').map(p => p.trim()).filter(Boolean)
    },
    extendedParagraphs() {
      const raw = this.text.split(this.marker).slice(1).join('')
      return raw.split('\n\n').map(p => p.trim()).filter(Boolean)
    }
  },
  watch: {
    text() {
      this.expanded = false
    }
  }
}
</script>

<style scoped>
.rm-text {
  margin: 0 0 0.5em;
}

.rm-para {
  margin-bottom: 1em;

  &:last-of-type {
    margin-bottom: 0;
  }
}

.rm-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: var(--font-primary);
  font-size: var(--font-sm);
  letter-spacing: 0.08em;
  color: var(--color-highlight);
  transition: color var(--transition-base);
  margin-top: 0.4em;

  &:hover {
    color: var(--color-highlight-hover);
  }
}

.rm-chevron {
  flex-shrink: 0;
}
</style>
